import { useRef } from 'react';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { observer } from '../../constants';
import styles from './PatientList.module.scss';
import { PatientListItem } from './components/PatientListItem/PatientListItem';
import cx from 'classnames';
import { Spinner } from 'components';
import { useIntersectionObserver } from 'hooks';
import { RootState } from 'store';
import { useSelector } from 'store/utils';
import { GetPatientCasesParams, PatientListInfo, Patient as PatientType } from 'types/patientCase';

type Props = {
  patients: Array<PatientType>;
  className?: string;
  itemClassname?: string;
  markSelected?: boolean;
  selectInfo: (state: RootState) => PatientListInfo | null;
  selectFilter: (state: RootState) => GetPatientCasesParams;
  setFilter: ActionCreatorWithPayload<GetPatientCasesParams, string>;
  scrollLoadingEnabled?: boolean;
  hideNextPageLoader?: boolean;
  showPatientCardControls?: boolean;
  isLoading: boolean;
  withPersistentMessageIcon?: boolean;
};

export const PatientList = ({
  patients,
  className,
  itemClassname,
  markSelected,
  selectInfo,
  selectFilter,
  setFilter,
  isLoading,
  scrollLoadingEnabled = true,
  hideNextPageLoader = false,
  showPatientCardControls = false,
  withPersistentMessageIcon = false,
}: Props) => {
  const dispatch = useDispatch();

  const ref = useRef<HTMLDivElement | null>(null);

  const patientListInfo = useSelector(selectInfo);
  const patientPageFilters = useSelector(selectFilter);

  const onIntersectionAction = () => {
    dispatch(
      setFilter({
        ...patientPageFilters,
        page: patientPageFilters.page ? patientPageFilters.page + 1 : 1,
      }),
    );
  };

  useIntersectionObserver({
    info: patientListInfo,
    IntersectionObject: ref,
    IntersectionObserverProps: { rootMargin: observer.rootMargin },
    dependencies: [patients],
    onIntersectionAction,
    scrollLoadingEnabled,
  });

  const showNextPageLoader = isLoading && patients.length && !hideNextPageLoader;

  return (
    <div className={cx(styles.patients, [className])}>
      {patients &&
        patients.map((el, index) => (
          <PatientListItem
            {...el}
            task={el.task}
            key={el.taskStatusUpdatedAt}
            className={itemClassname}
            markSelected={markSelected}
            showControls={showPatientCardControls}
            ref={index === patients.length - 1 ? ref : null}
            withPersistentMessageIcon={withPersistentMessageIcon}
          />
        ))}
      {showNextPageLoader && (
        <div className={styles.nextPageSpinnerContainer}>
          <Spinner isLoading />
        </div>
      )}
    </div>
  );
};
